import type { Connector, Diagram, LayoutCheckpoint, Operation, Point, Relationship } from "../../shared/contracts.js";
import { MAX_LAYOUT_CHECKPOINTS, MAX_LAYOUT_NAME, validLayoutCheckpoints } from "../../shared/layoutCheckpoints.js";
import type { CanvasBox, LayoutCandidate } from "./canvasGeometry.js";

const copy = <T>(value: T): T => structuredClone(value);
const samePoint = (a: Point, b: Point) => Math.abs(a.x - b.x) < 0.1 && Math.abs(a.y - b.y) < 0.1;
const nameKey = (name: string) => name.trim().normalize("NFKC").toLowerCase();
const geometry = (connector: Connector): Connector => {
  const { relationshipId, sourceSide, targetSide, sourceSlot, targetSlot, sourceFraction, targetFraction, routing, waypoints } = connector;
  return { relationshipId, ...(sourceSide === undefined ? {} : { sourceSide }),
    ...(targetSide === undefined ? {} : { targetSide }), ...(sourceSlot === undefined ? {} : { sourceSlot }),
    ...(targetSlot === undefined ? {} : { targetSlot }),
    ...(sourceFraction === undefined ? {} : { sourceFraction }), ...(targetFraction === undefined ? {} : { targetFraction }), ...(routing === undefined ? {} : { routing }),
    ...(waypoints === undefined ? {} : { waypoints: copy(waypoints) }) };
};
const ordered = (value: unknown): unknown => Array.isArray(value) ? value.map(ordered) :
  value && typeof value === "object" ? Object.fromEntries(Object.entries(value).filter(([, child]) => child !== undefined).sort(([a], [b]) => a.localeCompare(b)).map(([key, child]) => [key, ordered(child)])) : value;
const equal = (a: unknown, b: unknown) => JSON.stringify(ordered(a)) === JSON.stringify(ordered(b));

export function captureLayoutCheckpoint(
  diagram: Diagram,
  name: string,
  relationships: Relationship[] = [],
  boxes: CanvasBox[] = [],
): LayoutCheckpoint {
  const trimmed = name.trim();
  if (!trimmed || trimmed.length > MAX_LAYOUT_NAME) throw new Error(`Use a saved layout name of 1 to ${MAX_LAYOUT_NAME} characters.`);
  if ((diagram.layoutCheckpoints?.length ?? 0) >= MAX_LAYOUT_CHECKPOINTS) throw new Error(`Delete an older saved layout before adding more than ${MAX_LAYOUT_CHECKPOINTS}.`);
  if (diagram.layoutCheckpoints?.some((checkpoint) => nameKey(checkpoint.name) === nameKey(trimmed)))
    throw new Error("A saved layout already has this name. Choose another name.");
  const measured = new Map(boxes.map((box) => [box.id, box]));
  const visible = new Set(diagram.appearances.map((appearance) => appearance.objectId));
  const relationMap = new Map(relationships.map((relationship) => [relationship.relationshipId, relationship]));
  const connectorMap = new Map(diagram.connectors.map((connector) => [connector.relationshipId, connector]));
  // Record automatic routes as well, so a later manual route can return to automatic.
  for (const relationship of relationships)
    if (relationship.representationId === diagram.representationId && visible.has(relationship.sourceObjectId) && visible.has(relationship.targetObjectId) && !connectorMap.has(relationship.relationshipId))
      connectorMap.set(relationship.relationshipId, { relationshipId: relationship.relationshipId, routing: "auto" });
  const checkpoint: LayoutCheckpoint = {
    checkpointId: globalThis.crypto.randomUUID(), name: trimmed, createdAt: new Date().toISOString(),
    positions: diagram.appearances.map((appearance) => {
      const box = measured.get(appearance.appearanceId), width = box?.width ?? appearance.width, height = box?.height ?? appearance.height;
      return { appearanceId: appearance.appearanceId, objectId: appearance.objectId, position: copy(appearance.position),
        ...(width === undefined ? {} : { width }), ...(height === undefined ? {} : { height }) };
    }),
    connectors: [...connectorMap.values()].map((connector) => {
      const relationship = relationMap.get(connector.relationshipId);
      return { ...geometry(connector), ...(relationship ? { sourceObjectId: relationship.sourceObjectId, targetObjectId: relationship.targetObjectId } : {}) };
    }),
  };
  if (!validLayoutCheckpoints([checkpoint])) throw new Error("This diagram exceeds the saved layout geometry limits.");
  return checkpoint;
}

export function checkpointSaveOperations(diagram: Diagram, checkpoint: LayoutCheckpoint): Operation[] {
  const checkpoints = [...(diagram.layoutCheckpoints ?? []), copy(checkpoint)];
  if (!validLayoutCheckpoints(checkpoints)) throw new Error("Saved layout names and identities must be unique, with at most 30 saved layouts per diagram.");
  return [{ type: "diagram.update", diagramId: diagram.diagramId, changes: { layoutCheckpoints: checkpoints } }];
}

export function checkpointDeleteOperations(diagram: Diagram, checkpointId: string): Operation[] {
  if (!diagram.layoutCheckpoints?.some((checkpoint) => checkpoint.checkpointId === checkpointId)) return [];
  return [{ type: "diagram.update", diagramId: diagram.diagramId,
    changes: { layoutCheckpoints: diagram.layoutCheckpoints.filter((checkpoint) => checkpoint.checkpointId !== checkpointId) } }];
}

const intersects = (a: CanvasBox, b: CanvasBox, gap = 0) =>
  a.x < b.x + b.width + gap && a.x + a.width + gap > b.x &&
  a.y < b.y + b.height + gap && a.y + a.height + gap > b.y;

/** A bounded nearest-free search. The final rightmost column always has free space. */
function nearbyPlacement(box: CanvasBox, preferred: Point, occupied: CanvasBox[], preserveIfClear = false): Point {
  // Respect intentional tight spacing in the checkpoint when actual bounds do not overlap.
  if (preserveIfClear && !occupied.some((other) => intersects({ ...box, ...preferred }, other))) return preferred;
  const gap = 24, candidates: Point[] = [preferred], visited = new Set<string>();
  const score = (point: Point) => Math.abs(point.x - preferred.x) + Math.abs(point.y - preferred.y);
  for (let attempt = 0; candidates.length && attempt < 400; attempt++) {
    candidates.sort((a, b) => score(a) - score(b) || a.y - b.y || a.x - b.x);
    const point = candidates.shift()!, key = `${point.x},${point.y}`;
    if (visited.has(key)) continue;
    visited.add(key);
    const trial = { ...box, ...point }, conflict = occupied.find((other) => intersects(trial, other, gap));
    if (!conflict) return point;
    candidates.push(
      { x: conflict.x + conflict.width + gap, y: point.y },
      { x: conflict.x - box.width - gap, y: point.y },
      { x: point.x, y: conflict.y + conflict.height + gap },
      { x: point.x, y: conflict.y - box.height - gap },
    );
  }
  return { x: Math.max(preferred.x, ...occupied.map((other) => other.x + other.width + gap)), y: preferred.y };
}

export interface LayoutCheckpointPreview {
  candidate: LayoutCandidate;
  boxes: CanvasBox[];
  connectors: Connector[];
  operations: Operation[];
  added: number;
  removed: number;
  pinned: number;
  adjusted: number;
}

/** Produces a preview only. Apply operations as one command to retain atomic Undo/Redo. */
export function previewLayoutCheckpoint(
  diagram: Diagram,
  checkpoint: LayoutCheckpoint,
  boxes: CanvasBox[],
  relationships: Relationship[],
): LayoutCheckpointPreview {
  if (!validLayoutCheckpoints([checkpoint])) throw new Error("This saved layout contains invalid geometry.");
  const appearanceMap = new Map(diagram.appearances.map((appearance) => [appearance.appearanceId, appearance]));
  const boxMap = new Map(boxes.map((box) => [box.id, box]));
  const currentBoxes = diagram.appearances.map((appearance): CanvasBox => ({
    id: appearance.appearanceId, objectId: appearance.objectId, x: appearance.position.x, y: appearance.position.y,
    width: boxMap.get(appearance.appearanceId)?.width ?? appearance.width ?? 260,
    height: boxMap.get(appearance.appearanceId)?.height ?? appearance.height ?? 160,
    pinned: appearance.pinned,
  }));
  // A recreated appearance is a new item, even when its object survives.
  const saved = new Map(checkpoint.positions.filter((position) => appearanceMap.get(position.appearanceId)?.objectId === position.objectId).map((position) => [position.appearanceId, position]));
  const savedObjects = new Map([...saved.values()].map((position) => [position.objectId, position]));
  const positions: Record<string, Point> = {}, placed: CanvasBox[] = [], placedObjects = new Map<string, CanvasBox>();
  const add = (box: CanvasBox, point: Point) => {
    positions[box.id] = copy(point);
    const located = { ...box, ...point };
    placed.push(located); placedObjects.set(box.objectId, located);
  };
  let adjusted = 0;
  const pinned = currentBoxes.filter((box) => box.pinned);
  for (const box of pinned) add(box, box);
  const existing = currentBoxes.filter((box) => !box.pinned && saved.has(box.id)).sort((a, b) => a.id.localeCompare(b.id));
  for (const box of existing) {
    const preferred = saved.get(box.id)!.position;
    const point = nearbyPlacement(box, preferred, placed, true);
    if (!samePoint(point, preferred)) adjusted++;
    add(box, point);
  }
  const neighbors = new Map<string, Set<string>>();
  for (const relationship of relationships) {
    if (relationship.representationId !== diagram.representationId) continue;
    for (const [from, to] of [[relationship.sourceObjectId, relationship.targetObjectId], [relationship.targetObjectId, relationship.sourceObjectId]]) {
      if (!neighbors.has(from)) neighbors.set(from, new Set());
      neighbors.get(from)!.add(to);
    }
  }
  const addedBoxes = currentBoxes.filter((box) => !saved.has(box.id));
  const newMovable = addedBoxes.filter((box) => !box.pinned);
  while (newMovable.length) {
    // Place items connected to the restored model before disconnected additions.
    const known = (box: CanvasBox) => [...(neighbors.get(box.objectId) ?? [])].filter((id) => placedObjects.has(id));
    newMovable.sort((a, b) => known(b).length - known(a).length || a.id.localeCompare(b.id));
    const box = newMovable.shift()!, peers = known(box).map((id) => placedObjects.get(id)!);
    const preferred = peers.length ? {
      x: peers.reduce((sum, peer) => sum + peer.x + peer.width, 0) / peers.length + 60,
      y: peers.reduce((sum, peer) => sum + peer.y, 0) / peers.length,
    } : { x: box.x, y: box.y };
    add(box, nearbyPlacement(box, preferred, placed));
  }
  let overlaps = 0;
  for (let i = 0; i < placed.length; i++) for (let j = i + 1; j < placed.length; j++) if (intersects(placed[i]!, placed[j]!)) overlaps++;
  const removed = checkpoint.positions.length - saved.size;
  const warnings: string[] = [];
  if (removed) warnings.push(`${removed} saved ${removed === 1 ? "entity is" : "entities are"} no longer on this diagram and will be skipped.`);
  if (addedBoxes.length) warnings.push(`${addedBoxes.length} ${addedBoxes.length === 1 ? "new entity will be kept" : "new entities will be kept"} and placed in available space; pinned entities retain their current position.`);
  if (pinned.length) warnings.push(`${pinned.length} pinned ${pinned.length === 1 ? "entity will retain its" : "entities will retain their"} current position.`);
  if (adjusted) warnings.push(`${adjusted} saved ${adjusted === 1 ? "position was" : "positions were"} adjusted to avoid current entity bounds or pins. Current sizes and size locks are preserved.`);
  if (overlaps) warnings.push("Current pinned entities overlap. Resolve the pins before applying this saved layout.");

  const currentConnectors = new Map(diagram.connectors.map((connector) => [connector.relationshipId, connector]));
  const savedConnectors = new Map(checkpoint.connectors.map((connector) => [connector.relationshipId, connector]));
  const visible = new Set(currentBoxes.map((box) => box.objectId));
  const connectors = new Map(diagram.connectors.map((connector) => [connector.relationshipId, copy(connector)]));
  let rerouted = 0;
  for (const relationship of relationships) {
    if (relationship.representationId !== diagram.representationId || !visible.has(relationship.sourceObjectId) || !visible.has(relationship.targetObjectId)) continue;
    const old = currentConnectors.get(relationship.relationshipId), savedConnector = savedConnectors.get(relationship.relationshipId);
    if (!savedConnector) {
      // New relationships survive. Their manual bends must follow any moved endpoints.
      if (old?.routing === "manual" && [relationship.sourceObjectId, relationship.targetObjectId].some((id) => {
        const prior = currentBoxes.find((box) => box.objectId === id), next = placedObjects.get(id);
        return prior && next && !samePoint(prior, next);
      })) { connectors.set(relationship.relationshipId, { relationshipId: relationship.relationshipId, routing: "auto", ...(old.style ? { style: copy(old.style) } : {}) }); rerouted++; }
      continue;
    }
    const rewired = (savedConnector.sourceObjectId !== undefined && savedConnector.sourceObjectId !== relationship.sourceObjectId) ||
      (savedConnector.targetObjectId !== undefined && savedConnector.targetObjectId !== relationship.targetObjectId);
    const endpointsCompatible = !rewired && [relationship.sourceObjectId, relationship.targetObjectId].every((id) => {
      const prior = savedObjects.get(id), next = placedObjects.get(id);
      return prior && next && samePoint(prior.position, next) &&
        (prior.width === undefined || Math.abs(prior.width - next.width) < 0.1) &&
        (prior.height === undefined || Math.abs(prior.height - next.height) < 0.1);
    });
    const hasManualPath = savedConnector.routing === "manual" || !!savedConnector.waypoints?.length;
    let next: Connector;
    if (rewired || (hasManualPath && !endpointsCompatible)) {
      next = { relationshipId: relationship.relationshipId, routing: "auto" }; rerouted++;
    } else next = geometry(savedConnector);
    if (old?.style) next.style = copy(old.style);
    connectors.set(relationship.relationshipId, next);
  }
  if (rerouted) warnings.push(`${rerouted} relationship ${rerouted === 1 ? "route will be recalculated" : "routes will be recalculated"} because endpoints, sizes or positions changed.`);
  const operations: Operation[] = [];
  for (const box of currentBoxes) if (!box.pinned && !samePoint(box, positions[box.id]!))
    operations.push({ type: "appearance.update", diagramId: diagram.diagramId, appearanceId: box.id, changes: { position: copy(positions[box.id]!) } });
  for (const connector of connectors.values()) if (!equal(currentConnectors.get(connector.relationshipId), connector))
    operations.push({ type: "connector.set", diagramId: diagram.diagramId, connector });
  return {
    candidate: { positions, moved: operations.filter((operation) => operation.type === "appearance.update").length, overlaps, warnings,
      clusters: 1, fingerprint: JSON.stringify({ checkpointId: checkpoint.checkpointId, positions }) },
    boxes: currentBoxes.map((box) => ({ ...box, ...positions[box.id] })), connectors: [...connectors.values()], operations,
    added: addedBoxes.length, removed, pinned: pinned.length, adjusted,
  };
}
